import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import sankey from "highcharts/modules/sankey";
import { useIntl } from "react-intl";

import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import User from "@mapmycustomers/shared/types/User";
import { LoadingSpinner } from "@mapmycustomers/ui";

import DealListModalForClosedStages from "@app/component/DealListModalForCharts";
import ChartCard from "@app/scene/reports/components/charts/ChartCard";
import NoChart from "@app/scene/reports/components/charts/NoChart";
import { fetchDealsByStage } from "@app/scene/reports/store/actions";
import FunnelStatsData from "@app/scene/reports/store/FunnelStatsData";
import { getFunnelsStatsData } from "@app/scene/reports/store/selectors";
import { RootState } from "@app/store/rootReducer";
import isClosedStage from "@app/util/isClosedStage";

import DealListModal from "./DealListModal";
import useChartConfig from "./useChartConfig";

sankey(Highcharts);

interface Props {
  funnel: Funnel | undefined;
  funnelsStatsData: FunnelStatsData;
  userIds: Array<User["id"]>;
}

const DealsProgressionChart: React.FC<
  Props & typeof mapDispatchToProps & ReturnType<typeof mapStateToProps>
> = ({ funnel, funnelsStatsData, userIds }) => {
  const intl = useIntl();
  const [selectedStage, setSelectedStage] = useState<Stage | undefined>(undefined);

  const title = intl.formatMessage({
    id: "reports.deals.progressionChart",
    defaultMessage: "Deal Progression Through Pipeline",
  });

  const subtitle = intl.formatMessage({
    id: "reports.deals.progressionChart.subtitle",
    defaultMessage:
      "Visualize the number of deals that have reached each stage over time. Click on a stage to see deals actively in that stage.",
  });

  const handleSelectStage = useCallback(
    (stage: Stage) => {
      setSelectedStage(stage);
    },
    [setSelectedStage]
  );

  const options = useChartConfig(funnelsStatsData.stageSummary, title, subtitle, handleSelectStage);

  return (
    <>
      <ChartCard>
        {funnelsStatsData.loading ? (
          <LoadingSpinner />
        ) : options ? (
          <HighchartsReact highcharts={Highcharts} immutable options={options} />
        ) : (
          <NoChart chartTitle={title} />
        )}
      </ChartCard>
      {selectedStage && !isClosedStage(selectedStage) && (
        <DealListModal
          onHide={() => setSelectedStage(undefined)}
          stage={selectedStage}
          userIds={userIds}
        />
      )}
      {selectedStage && isClosedStage(selectedStage) && (
        <DealListModalForClosedStages
          funnelName={funnel?.name}
          onHide={() => setSelectedStage(undefined)}
          stage={selectedStage}
          userIds={userIds}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  funnelsStatsData: getFunnelsStatsData(state),
});

const mapDispatchToProps = {
  fetchDealsByStageId: fetchDealsByStage.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(DealsProgressionChart);
